import type { BoardEvent, EventUpdatePayload } from "../calendar-types"

export const localDayKey = (day: Date): string =>
  `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`

const zoneFormatters = new Map<string, Intl.DateTimeFormat>()
const zonedParts = (date: Date, timezone: string) => {
  let formatter = zoneFormatters.get(timezone)

  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    })
    zoneFormatters.set(timezone, formatter)
  }

  const parts = formatter.formatToParts(date)
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value)

  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
  }
}
const wallTimestamp = (date: Date, timezone: string): number => {
  const p = zonedParts(date, timezone)

  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, date.getUTCMilliseconds())
}
export const zonedDayKey = (date: Date, timezone: string): string => {
  const p = zonedParts(date, timezone)

  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`
}
export const formatZonedTime = (date: Date, timezone: string): string =>
  new Intl.DateTimeFormat("de-DE", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)

const resolveWallTime = (desired: number, sourceOffset: number, timezone: string): Date => {
  const offsets = new Set<number>([sourceOffset])
  for (let hours = -36; hours <= 36; hours += 6) {
    const sample = new Date(desired + hours * 3600000)
    offsets.add(wallTimestamp(sample, timezone) - sample.getTime())
  }
  const candidates = [...offsets]
    .map((offset) => new Date(desired - offset))
    .filter((date) => wallTimestamp(date, timezone) === desired)
    .sort((a, b) => a.getTime() - b.getTime())
  const result =
    candidates.find((date) => wallTimestamp(date, timezone) - date.getTime() === sourceOffset) ??
    candidates[0]

  if (!result)
    throw new Error("Diese Uhrzeit existiert am gewählten Tag wegen der Zeitumstellung nicht.")

  return result
}

/** Resolve a wall-clock time, rejecting DST gaps and preferring the source offset in a fold. */
export const moveInstantToDay = (original: Date, targetDay: string, timezone: string): Date => {
  const [year, month, day] = targetDay.split("-").map(Number)
  const time = zonedParts(original, timezone)
  const desired = Date.UTC(
    year,
    month - 1,
    day,
    time.hour,
    time.minute,
    time.second,
    original.getUTCMilliseconds(),
  )

  return resolveWallTime(desired, wallTimestamp(original, timezone) - original.getTime(), timezone)
}

/** Calendar windows are wall-clock minutes in the organisation's timezone, including day-end 1440. */
export const calendarDayInstant = (dayKey: string, minute: number, timezone: string): Date => {
  const [year, month, day] = dayKey.split("-").map(Number)

  return resolveWallTime(Date.UTC(year, month - 1, day, 0, minute), 0, timezone)
}

export const buildOverviewMove = (
  item: BoardEvent,
  targetDay: string,
  timezone: string,
): EventUpdatePayload | null => {
  const originalStart = new Date(item.event.start_at)
  const originalEnd = new Date(item.event.end_at)
  const duration = originalEnd.getTime() - originalStart.getTime()

  if (!Number.isFinite(duration) || duration <= 0)
    throw new Error("Der Termin hat keine gültige Dauer.")

  if (zonedDayKey(originalStart, timezone) === targetDay) return null

  const start = moveInstantToDay(originalStart, targetDay, timezone)
  const end = new Date(start.getTime() + duration)

  return {
    event: item.event,
    start,
    end,
    employeeId: item.event.employee_id,
    flexType: "HARD",
    timeframeStart: start,
    timeframeEnd: end,
  }
}
